import React, { useState, useEffect } from 'react';
import { partService } from '../../services/partService'; // 🚀 부품 자산 데이터베이스 파이프라인 정밀 연동

const AsSettle = ({
  activeRecord,
  settleDate, setSettleDate,
  newPart, setNewPart, initialPart,
  repairParts, setRepairParts,
  discount, setDiscount,
  handleSettleSave,
  setViewMode, setIsEditing
}) => {
  // 🚀 부품 DB 실시간 연동 및 자동완성 매핑용 상태 제어계 내장
  const [partsList, setPartsList] = useState([]);
  const [filteredParts, setFilteredParts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // 컴포넌트 렌더링 시 전사 부품 마스터 데이터 실시간 프리로드
  useEffect(() => {
    let isMounted = true;
    const loadPartsData = async () => {
      try {
        const data = await partService.getParts();
        if (isMounted) {
          setPartsList(data || []);
        }
      } catch (err) {
        console.error("정산 폼용 부품 데이터 연동 오류:", err);
      }
    };
    loadPartsData();
    return () => { isMounted = false; };
  }, []);

  // 정산 폼 전용 스타일 (기존 As.js에 있던 스타일)
  const styles = {
    card: { background: 'white', padding: '40px', borderRadius: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', marginBottom: '35px', border: '1px solid #edf2f7' },
    sectionTitle: { fontSize: '20px', fontWeight: '800', color: '#1e293b', marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '12px' },
    label: { fontSize: '13px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '8px' },
    input: { width: '100%', padding: '14px 18px', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' },
    grid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '30px', marginBottom: '30px' },
    btnMain: { padding: '16px 32px', background: '#1e293b', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' },
    btnGhost: { padding: '10px 20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', color: '#64748b' },
    // 🚀 자동완성 목록용 절대 좌표 오버레이 레이아웃 배치
    dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #cbd5e1', borderRadius: '12px', maxHeight: '180px', overflowY: 'auto', zIndex: 9999, boxShadow: '0 10px 25px rgba(0,0,0,0.1)', marginTop: '6px', listStyle: 'none', padding: 0 },
    dropdownItem: { padding: '12px 16px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.15s' }
  };

  // 🚀 검색 통합 필터 함수: 숫자형 데이터 충돌을 막기 위한 String() 캡슐화 가드 장착 완료
  const handleSearch = (val) => {
    const query = String(val).trim().toLowerCase();
    
    if (query) {
      const filtered = partsList.filter(p => 
        String(p.partName || '').toLowerCase().includes(query) ||
        String(p.taskName || '').toLowerCase().includes(query) ||
        String(p.partNo || '').toLowerCase().includes(query)
      );
      setFilteredParts(filtered);
    } else {
      setFilteredParts(partsList.slice(0, 50)); // 검색어가 없으면 상위 50개 기본 추천
    }
    setShowDropdown(true);
  };

  // 🚀 정산 금액 계산 (할인 반영)
  const supplyValue = Math.round(repairParts.reduce((sum, p) => sum + (Number(p.price || 0) * Number(p.qty || 0)) + Number(p.labor || 0), 0));
  const vatValue = Math.round(supplyValue * 0.1);
  const grossValue = supplyValue + vatValue;               // 할인 전 총액
  const discountValue = Number(discount) || 0;             // 할인액
  const finalValue = grossValue - discountValue;           // 최종 결제 금액

  return (
    <div style={styles.card}>
      <div style={styles.sectionTitle}>💰 정산 내역 입력</div>
      <div style={{ ...styles.grid4, background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
        <div><span style={styles.label}>고객명</span><b>{activeRecord?.customerName} {activeRecord?.carNumber ? `(${activeRecord?.carNumber})` : ''}</b></div>
        <div><span style={styles.label}>담당자</span><b>{activeRecord?.mechanic}</b></div>
        <div><span style={styles.label}>유형</span><b>{activeRecord?.serviceCategory || '정비'}</b></div>
        <div><span style={styles.label}>정산 일자</span><input type="date" style={styles.input} value={settleDate} onChange={e => setSettleDate(e.target.value)} /></div>
      </div>

      <div style={{ marginBottom: '20px', padding: '20px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '15px' }}>🔧 부품 및 공임 추가</div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr 1.5fr 1.5fr auto', gap: '10px', alignItems: 'end' }}>
          
          {/* 드롭다운이 인풋바 하단에 알맞게 정렬되도록 relative 부모 가드 선언 */}
          <div style={{ position: 'relative' }}>
            <span style={{ fontSize: '12px', color: '#64748b' }}>작업명/부품명</span>
            <input 
              style={styles.input} 
              value={newPart.taskName} 
              placeholder="부품명 또는 부품번호 검색..."
              onChange={e => {
                const val = e.target.value;
                setNewPart({...newPart, taskName: val});
                handleSearch(val);
              }} 
              onFocus={(e) => {
                handleSearch(e.target.value);
              }}
              onBlur={() => setShowDropdown(false)}
            />
            
            {/* 🚀 실시간 매칭 부품 추천 오버레이 레이어 활성화 (onMouseDown으로 포커스 이탈 방어) */}
            {showDropdown && (
              <ul style={styles.dropdown} onMouseDown={e => e.preventDefault()}>
                {filteredParts.length > 0 ? (
                  filteredParts.map((p, i) => (
                    <li
                      key={i}
                      style={styles.dropdownItem}
                      onClick={() => {
                        setNewPart({
                          ...newPart,
                          taskName: p.partName || p.taskName || '',
                          partNo: p.partNo || '',   // 재고 차감 시 이름 추측이 아니라 품번으로 정확히 매칭되도록
                          price: Number(p.outgoingPrice || p.supplyPrice || p.consumerPrice || p.price || 0)
                        });
                        setShowDropdown(false);
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div>
                        <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '13px' }}>{p.partName || p.taskName}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>번호: {p.partNo || '-'} | 현재고: {p.stock || 0} EA</div>
                      </div>
                      <div style={{ fontWeight: 'bold', color: '#10b981', fontSize: '13px' }}>
                        ₩ {Number(p.outgoingPrice || p.supplyPrice || p.consumerPrice || p.price || 0).toLocaleString()}
                      </div>
                    </li>
                  ))
                ) : (
                  // 🚀 검색 결과가 없을 때 시스템이 정상 작동 중임을 알려주는 피드백 영역 출력
                  <li style={{ padding: '16px', fontSize: '13px', color: '#64748b', textAlign: 'center' }}>
                    검색된 부품이 없습니다. <br/><span style={{fontSize: '11px'}}>(수동으로 입력하신 부품명으로 추가됩니다)</span>
                  </li>
                )}
              </ul>
            )}
          </div>

          <div><span style={{ fontSize: '12px', color: '#64748b' }}>구분</span>
            <select style={styles.input} value={newPart.type} onChange={e => setNewPart({...newPart, type: e.target.value})}>
              <option value="A">A(신품)</option><option value="B">B(재제조)</option><option value="C">C(중고)</option><option value="D">D(대체)</option><option value="F">F(수입)</option>
            </select>
          </div>
          <div><span style={{ fontSize: '12px', color: '#64748b' }}>수량</span><input type="number" style={styles.input} value={newPart.qty} onChange={e => setNewPart({...newPart, qty: Number(e.target.value)})} /></div>
          <div><span style={{ fontSize: '12px', color: '#64748b' }}>단가(원)</span><input type="number" style={styles.input} value={newPart.price} onChange={e => setNewPart({...newPart, price: Number(e.target.value)})} /></div>
          <div><span style={{ fontSize: '12px', color: '#64748b' }}>기술료(원)</span><input type="number" style={styles.input} value={newPart.labor} onChange={e => setNewPart({...newPart, labor: Number(e.target.value)})} /></div>
          <div><span style={{ fontSize: '12px', color: '#64748b' }}>비고</span><input style={styles.input} value={newPart.note} onChange={e => setNewPart({...newPart, note: e.target.value})} /></div>
          <button type="button" style={{ ...styles.btnMain, padding: '14px 20px' }} onClick={() => {
            if(!newPart.taskName) return alert('작업명/부품명을 입력하세요.');
            setRepairParts([...repairParts, newPart]);
            setNewPart(initialPart);
          }}>+</button>
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead style={{ background: '#f8fafc' }}>
          <tr>
            <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>작업명/부품명</th>
            <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>구분</th>
            <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>수량</th>
            <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>단가</th>
            <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>기술료</th>
            <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>합계</th>
            <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>삭제</th>
          </tr>
        </thead>
        <tbody>
          {repairParts.map((p, idx) => {
            const sum = (Number(p.price || 0) * Number(p.qty || 0)) + Number(p.labor || 0);
            return (
              <tr key={idx}>
                <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>{p.taskName}</td>
                <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>{p.type}</td>
                <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>{p.qty}</td>
                <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>₩ {Number(p.price).toLocaleString()}</td>
                <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>₩ {Number(p.labor).toLocaleString()}</td>
                <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 'bold' }}>₩ {sum.toLocaleString()}</td>
                <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <button onClick={() => setRepairParts(repairParts.filter((_, i) => i !== idx))} style={{ color: 'red', border: 'none', background: 'none', cursor: 'pointer' }}>✖</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ background: '#f1f5f9', padding: '20px', borderRadius: '12px', textAlign: 'right' }}>
        <div style={{ fontSize: '14px', marginBottom: '8px' }}>공급가액: ₩ {supplyValue.toLocaleString()}</div>
        <div style={{ fontSize: '14px', marginBottom: '8px' }}>부가세(VAT): ₩ {vatValue.toLocaleString()}</div>

        {/* 🚀 할인 입력 (부가세 면제 등) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px', color: '#ef4444', fontWeight: '700' }}>할인 (−):</span>
          <span style={{ fontSize: '14px', color: '#ef4444' }}>₩</span>
          <input
            type="number"
            min="0"
            value={discount}
            onChange={e => setDiscount(Number(e.target.value) || 0)}
            style={{ width: '140px', padding: '8px 12px', border: '1px solid #fecaca', borderRadius: '8px', textAlign: 'right', fontSize: '14px', color: '#ef4444', outline: 'none' }}
          />
          <button type="button" onClick={() => setDiscount(vatValue)}
            style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '700', color: '#475569' }}
            title="부가세 지급대상이 아닌 고객에게 부가세만큼 할인">
            부가세만큼
          </button>
          <button type="button" onClick={() => setDiscount(0)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '12px', color: '#94a3b8' }}>
            초기화
          </button>
        </div>

        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b', borderTop: '1px dashed #cbd5e1', paddingTop: '10px' }}>
          총 결제 금액: ₩ {finalValue.toLocaleString()}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '30px' }}>
        <button onClick={() => { setViewMode('LIST'); setIsEditing(false); }} style={styles.btnGhost}>취소 / 목록으로</button>
        <button onClick={handleSettleSave} style={{ ...styles.btnMain, background: '#10b981' }}>정산 및 상태 저장 완료</button>
      </div>
    </div>
  );
};

export default AsSettle;